import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
    TrashIcon,
    PencilSquareIcon,
    PlusIcon,
    Cog6ToothIcon,
    DocumentTextIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    CalendarDaysIcon,
    MapPinIcon,
    ArrowsRightLeftIcon,
    GlobeAmericasIcon,
    GiftIcon,
    ArchiveBoxArrowDownIcon,
    UserMinusIcon // Icon for Probation
} from '@heroicons/react/24/outline';

const PolicySettings = () => {
    const [activeTab, setActiveTab] = useState('types');
    const [countryList, setCountryList] = useState(['All Regions', 'India', 'USA', 'UK', 'Netherlands', 'UAE', 'Global']);
    const [selectedCountry, setSelectedCountry] = useState('All Regions');
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [policyText, setPolicyText] = useState('');
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalType, setModalType] = useState('');

    const fetchPolicyData = async (country) => {
        try {
            setLoading(true);
            let query = '';
            if (country !== 'All Regions') {
                query = `?country=${country}`;
            }
            const settingsRes = await api.get(`/settings${query}`);
            setLeaveTypes(settingsRes.data.leaveTypes);
            setHolidays(settingsRes.data.holidays);

            if (!policyText) {
                const docRes = await api.get('/leaves/dashboard');
                setPolicyText(docRes.data.policy || '');
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPolicyData(selectedCountry);
        // eslint-disable-next-line
    }, [selectedCountry]);

    const openTypeModal = (type) => {
        setModalType('type');
        setModalData(type || {
            id: null,
            name: '',
            annual_quota: 12,
            approval_levels: 1,
            is_comp_off: false,
            is_carry_forward: false,
            is_probation: false, // Default false
            is_unpaid: false,
            description: '',
            country: selectedCountry === 'All Regions' ? 'Global' : selectedCountry
        });
        setIsModalOpen(true);
    };

    const openHolidayModal = (holiday) => {
        setModalType('holiday');
        setModalData(holiday || {
            id: null,
            name: '',
            holiday_date: moment().format('YYYY-MM-DD'),
            country: selectedCountry === 'All Regions' ? 'Global' : selectedCountry
        });
        setIsModalOpen(true);
    };

    const handleSaveType = async (e) => {
        e.preventDefault();
        try {
            const dataToSave = {
                ...modalData,
                country: modalData.country || 'Global',
                // If special type, quota is 0 (Managed manually/earned)
                annual_quota: (modalData.is_comp_off || modalData.is_carry_forward || modalData.is_probation || modalData.is_unpaid) ? 0 : parseInt(modalData.annual_quota),
                approval_levels: parseInt(modalData.approval_levels),
                is_comp_off: modalData.is_comp_off,
                is_carry_forward: modalData.is_carry_forward,
                is_probation: modalData.is_probation,
                is_unpaid: modalData.is_unpaid
            };

            await api.post('/settings/leavetypes', dataToSave);
            toast.success(modalData.id ? "Leave Type Updated" : "Leave Type Created");
            setIsModalOpen(false);
            fetchPolicyData(selectedCountry);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to save");
        }
    };

    const handleSaveHoliday = async (e) => {
        e.preventDefault();
        try {
            const dataToSave = { ...modalData, country: modalData.country || 'Global' };
            await api.post('/settings/holidays', dataToSave);
            toast.success("Holiday Saved");
            setIsModalOpen(false);
            fetchPolicyData(selectedCountry);
        } catch (err) { toast.error(err.response?.data?.error || "Failed to save"); }
    };

    const handleDelete = async (id, type) => {
        if (!window.confirm(`Are you sure you want to delete this?`)) return;
        try {
            if (type === 'type') {
                await api.delete(`/settings/leavetypes/${id}`);
            } else {
                await api.delete(`/settings/holidays/${id}`);
            }
            toast.success("Deleted Successfully");
            fetchPolicyData(selectedCountry);
        } catch (err) { toast.error("Error during deletion"); }
    };

    const getWorkflowBadge = (t) => {
        if (t.is_unpaid) {
            return (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-rose-50 text-rose-700 ring-1 ring-rose-600/20 whitespace-nowrap">
                    <UserMinusIcon className="w-3.5 h-3.5 mr-1.5" /> Loss of Pay
                </span>
            );
        }
        if (t.is_probation) {
            return (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-700 ring-1 ring-slate-600/20 whitespace-nowrap">
                    <UserMinusIcon className="w-3.5 h-3.5 mr-1.5" /> Probation
                </span>
            );
        }
        if (t.is_comp_off) {
            return (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-amber-50 text-amber-700 ring-1 ring-amber-600/20 whitespace-nowrap">
                    <GiftIcon className="w-3.5 h-3.5 mr-1.5" /> Earned
                </span>
            );
        }
        if (t.is_carry_forward) {
            return (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-cyan-50 text-cyan-700 ring-1 ring-cyan-600/20 whitespace-nowrap">
                    <ArchiveBoxArrowDownIcon className="w-3.5 h-3.5 mr-1.5" /> Manual
                </span>
            );
        }
        if (t.approval_levels === 2) {
            return (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-purple-50 text-purple-700 ring-1 ring-purple-600/20 whitespace-nowrap">
                    <ShieldCheckIcon className="w-3.5 h-3.5 mr-1.5" /> 2 Levels
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-blue-50 text-blue-700 ring-1 ring-blue-600/20 whitespace-nowrap">
                <UserGroupIcon className="w-3.5 h-3.5 mr-1.5" /> 1 Level
            </span>
        );
    };

    if (loading) return (
        <Layout>
            <div className="flex flex-col items-center justify-center h-[80vh] text-slate-400 animate-pulse">
                <Cog6ToothIcon className="h-16 w-16 mb-4 opacity-50" />
                <p className="font-medium">Loading Settings...</p>
            </div>
        </Layout>
    );

    return (
        <Layout>
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight">Policy Configuration</h1>
                <div className="flex items-center space-x-3 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm w-full md:w-auto">
                    <div className="pl-3 pr-2 text-slate-400">
                        <MapPinIcon className="w-5 h-5" />
                    </div>
                    <select
                        value={selectedCountry}
                        onChange={e => setSelectedCountry(e.target.value)}
                        className="bg-transparent text-sm font-bold text-slate-700 focus:outline-none cursor-pointer pr-4 py-1 w-full"
                    >
                        {countryList.map(country => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex overflow-x-auto space-x-4 border-b border-slate-200 mb-8 pb-1 scrollbar-hide">
                <button onClick={() => setActiveTab('types')} className={`pb-3 px-3 text-base sm:text-lg font-semibold border-b-4 transition-colors whitespace-nowrap ${activeTab === 'types' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                    Leave Types
                </button>
                <button onClick={() => setActiveTab('holidays')} className={`pb-3 px-3 text-base sm:text-lg font-semibold border-b-4 transition-colors whitespace-nowrap ${activeTab === 'holidays' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                    Public Holidays
                </button>
                {/* <button onClick={() => setActiveTab('document')} className={`pb-3 px-3 text-base sm:text-lg font-semibold border-b-4 transition-colors whitespace-nowrap ${activeTab === 'document' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                    Policy Document
                </button> */}
            </div>

            {/* Leave Types Tab */}
            {activeTab === 'types' && (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-slate-50/50 gap-4">
                        <h3 className="font-bold text-lg text-slate-800">
                            {selectedCountry === 'All Regions' ? 'All Leave Types' : `Leave Types for ${selectedCountry}`}
                        </h3>
                        <button onClick={() => openTypeModal(null)} className="flex items-center text-sm bg-slate-900 text-white px-4 py-2 rounded-xl hover:bg-indigo-600 shadow-md transition-all hover:-translate-y-0.5 w-full sm:w-auto justify-center">
                            <PlusIcon className="h-4 w-4 mr-2" /> New Type
                        </button>
                    </div>

                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-left border-collapse min-w-[800px]">
                            <thead>
                                <tr className="text-xs text-slate-400 uppercase tracking-wider font-bold border-b border-slate-100 bg-slate-50/30">
                                    <th className="px-6 py-3 w-[25%]">Name</th>
                                    <th className="px-6 py-3 w-[15%]">Quota</th>
                                    <th className="px-6 py-3 w-[25%]">Workflow</th>
                                    <th className="px-6 py-3 w-[15%]">Region</th>
                                    <th className="px-6 py-3 w-[20%] text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm divide-y divide-slate-50">
                                {leaveTypes.map(t => (
                                    <tr key={t.id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-800">{t.name}</div>
                                            {t.description && <div className="text-xs text-slate-400 mt-0.5 italic max-w-xs truncate">{t.description}</div>}
                                        </td>
                                        <td className="px-6 py-4">
                                            {t.is_unpaid ? (
                                                <span className="text-xs font-bold text-rose-600 bg-rose-50 px-2 py-1 rounded">Unpaid</span>
                                            ) : t.is_probation ? (
                                                <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">Probation</span>
                                            ) : t.is_comp_off ? (
                                                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded">Earned</span>
                                            ) : t.is_carry_forward ? (
                                                <span className="text-xs font-bold text-cyan-600 bg-cyan-50 px-2 py-1 rounded">Manual</span>
                                            ) : (
                                                <><span className="text-xl font-black text-indigo-600">{t.annual_quota}</span> <span className="text-xs font-bold text-slate-400 ml-1">Days</span></>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">{getWorkflowBadge(t)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-md text-xs font-bold flex w-fit items-center ${t.country === 'Global' ? 'bg-slate-100 text-slate-600' : 'bg-indigo-50 text-indigo-600'}`}>
                                                {t.country === 'Global' ? <GlobeAmericasIcon className="w-3 h-3 mr-1" /> : <MapPinIcon className="w-3 h-3 mr-1" />}
                                                {t.country}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button onClick={() => openTypeModal(t)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Edit">
                                                    <PencilSquareIcon className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => handleDelete(t.id, 'type')} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all" title="Delete">
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {leaveTypes.length === 0 && <tr><td colSpan="5" className="px-6 py-12 text-center text-slate-400">No leave types found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Public Holidays Tab (Same as before) */}
            {activeTab === 'holidays' && (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-slate-50/50 gap-4">
                        <h3 className="font-bold text-lg text-slate-800">
                            {selectedCountry === 'All Regions' ? 'All Public Holidays' : `Public Holidays for ${selectedCountry}`}
                        </h3>
                        <button onClick={() => openHolidayModal(null)} className="flex items-center text-sm bg-slate-900 text-white px-4 py-2 rounded-xl hover:bg-indigo-600 shadow-md transition-all hover:-translate-y-0.5 w-full sm:w-auto justify-center">
                            <PlusIcon className="h-4 w-4 mr-2" /> Add Holiday
                        </button>
                    </div>
                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-left border-collapse min-w-[700px]">
                            <thead>
                                <tr className="text-xs text-slate-400 uppercase tracking-wider font-bold border-b border-slate-100 bg-slate-50/30">
                                    <th className="px-6 py-3 w-[35%]">Holiday Name</th>
                                    <th className="px-6 py-3 w-[25%]">Date</th>
                                    <th className="px-6 py-3 w-[25%]">Region</th>
                                    <th className="px-6 py-3 w-[15%] text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm divide-y divide-slate-50">
                                {holidays.map(h => (
                                    <tr key={h.id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-6 py-4 font-bold text-slate-800">{h.name}</td>
                                        <td className="px-6 py-4 font-medium text-indigo-600 flex items-center">
                                            <CalendarDaysIcon className="w-4 h-4 mr-2 text-indigo-400" />
                                            {moment(h.holiday_date).format('MMMM Do, YYYY')}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-md text-xs font-bold flex w-fit items-center ${h.country === 'Global' ? 'bg-slate-100 text-slate-600' : 'bg-orange-50 text-orange-600'}`}>
                                                {h.country === 'Global' ? <GlobeAmericasIcon className="w-3 h-3 mr-1" /> : <MapPinIcon className="w-3 h-3 mr-1" />}
                                                {h.country}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button onClick={() => openHolidayModal(h)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Edit"><PencilSquareIcon className="w-5 h-5" /></button>
                                                <button onClick={() => handleDelete(h.id, 'holiday')} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all" title="Delete"><TrashIcon className="w-5 h-5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {holidays.length === 0 && <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-400">No public holidays found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Document Tab */}
            {activeTab === 'document' && (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="px-6 py-6 border-b border-slate-100 flex items-center bg-slate-50/50">
                        <DocumentTextIcon className="h-6 w-6 text-indigo-500 mr-3" />
                        <h3 className="font-bold text-lg text-slate-800">Company Policy</h3>
                    </div>
                    <div className="p-6 sm:p-8 prose prose-indigo max-w-none text-sm sm:text-base">
                        <div dangerouslySetInnerHTML={{ __html: policyText || '<p>No policy document defined.</p>' }}></div>
                    </div>
                </div>
            )}

            {/* --- ADD/EDIT MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 animate-fade-in-up border border-white/20 overflow-y-auto max-h-[90vh]">
                        <div className="mb-6">
                            <h3 className="text-2xl font-black text-slate-800">{modalData.id ? 'Edit' : 'New'} {modalType === 'type' ? 'Leave Type' : 'Public Holiday'}</h3>
                        </div>

                        {/* FORM: LEAVE TYPE */}
                        {modalType === 'type' && (
                            <form onSubmit={handleSaveType} className="space-y-5">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Name</label>
                                    <input className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold focus:ring-2 focus:ring-indigo-500 transition-all outline-none" placeholder="e.g. Sick Leave" value={modalData.name} onChange={e => setModalData({ ...modalData, name: e.target.value })} required />
                                </div>

                                {/* Hide Quota if Special Type */}
                                {!modalData.is_comp_off && !modalData.is_carry_forward && !modalData.is_probation && !modalData.is_unpaid && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Annual Quota (Days)</label>
                                        <input className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold focus:ring-2 focus:ring-indigo-500 transition-all outline-none" type="number" placeholder="e.g. 10" value={modalData.annual_quota} onChange={e => setModalData({ ...modalData, annual_quota: e.target.value })} required />
                                    </div>
                                )}

                                {/* --- COMP OFF CHECKBOX --- */}
                                <div className="flex items-start bg-amber-50 p-4 rounded-xl border border-amber-100">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="is_comp_off"
                                            type="checkbox"
                                            className="h-5 w-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                                            checked={modalData.is_comp_off || false}
                                            onChange={e => setModalData({ ...modalData, is_comp_off: e.target.checked, is_carry_forward: false, is_probation: false, is_unpaid: false })}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="is_comp_off" className="font-bold text-amber-900 cursor-pointer">Comp Off / Earned Leave?</label>
                                        <p className="text-amber-700/70 text-xs mt-1">Balances start at 0 and are earned via requests.</p>
                                    </div>
                                </div>

                                {/* --- CARRY FORWARD CHECKBOX --- */}
                                <div className="flex items-start bg-cyan-50 p-4 rounded-xl border border-cyan-100">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="is_carry_forward"
                                            type="checkbox"
                                            className="h-5 w-5 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500 cursor-pointer"
                                            checked={modalData.is_carry_forward || false}
                                            onChange={e => setModalData({ ...modalData, is_carry_forward: e.target.checked, is_comp_off: false, is_probation: false, is_unpaid: false })}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="is_carry_forward" className="font-bold text-cyan-900 cursor-pointer">Carry Forward / Manual Balance?</label>
                                        <p className="text-cyan-700/70 text-xs mt-1">Balances are set manually by Admin per user (Start at 0).</p>
                                    </div>
                                </div>

                                {/* --- PROBATION CHECKBOX --- */}
                                <div className="flex items-start bg-slate-50 p-4 rounded-xl border border-slate-200">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="is_probation"
                                            type="checkbox"
                                            className="h-5 w-5 text-slate-600 rounded border-gray-300 focus:ring-slate-500 cursor-pointer"
                                            checked={modalData.is_probation || false}
                                            onChange={e => setModalData({ ...modalData, is_probation: e.target.checked, is_comp_off: false, is_carry_forward: false, is_unpaid: false })}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="is_probation" className="font-bold text-slate-900 cursor-pointer">Probationary Leave?</label>
                                        <p className="text-slate-500 text-xs mt-1">Only visible to employees in 'Probation' status. Manual balance.</p>
                                    </div>
                                </div>

                                {/* --- LOSS OF PAY CHECKBOX --- */}
                                <div className="flex items-start bg-rose-50 p-4 rounded-xl border border-rose-100">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="is_unpaid"
                                            type="checkbox"
                                            className="h-5 w-5 text-rose-600 rounded border-gray-300 focus:ring-rose-500 cursor-pointer"
                                            checked={!!modalData.is_unpaid}
                                            onChange={e => setModalData({ ...modalData, is_unpaid: e.target.checked, is_comp_off: false, is_carry_forward: false, is_probation: false })}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="is_unpaid" className="font-bold text-rose-900 cursor-pointer">Loss of Pay (Unpaid)?</label>
                                        <p className="text-rose-700/70 text-xs mt-1">No balance needed. Every day taken is recorded as LOP.</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Region</label>
                                    <select className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all outline-none" value={modalData.country} onChange={e => setModalData({ ...modalData, country: e.target.value })}>
                                        {countryList.filter(c => c !== 'All Regions').map(country => (
                                            <option key={country} value={country}>{country}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Approval Workflow</label>
                                    <div className="relative">
                                        <select
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all outline-none appearance-none"
                                            value={modalData.approval_levels}
                                            onChange={e => setModalData({ ...modalData, approval_levels: e.target.value })}
                                        >
                                            <option value="1">Level 1 (Reporting Manager Only)</option>
                                            <option value="2">Level 2 (Manager + 2nd Approver)</option>
                                        </select>
                                        <ArrowsRightLeftIcon className="w-5 h-5 absolute right-4 top-3.5 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Description</label>
                                    <textarea className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all outline-none resize-none" placeholder="Brief description..." rows="2" value={modalData.description} onChange={e => setModalData({ ...modalData, description: e.target.value })} />
                                </div>
                                <div className="flex justify-end space-x-3 pt-2">
                                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition">Cancel</button>
                                    <button type="submit" className="px-8 py-3 bg-slate-900 text-white font-bold rounded-xl shadow-lg hover:bg-indigo-600 hover:shadow-indigo-500/30 transition-all transform hover:-translate-y-0.5">Save</button>
                                </div>
                            </form>
                        )}

                        {/* FORM: PUBLIC HOLIDAY */}
                        {modalType === 'holiday' && (
                            <form onSubmit={handleSaveHoliday} className="space-y-5">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Holiday Name</label>
                                    <input className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold focus:ring-2 focus:ring-indigo-500 transition-all outline-none" placeholder="e.g. Christmas" value={modalData.name} onChange={e => setModalData({ ...modalData, name: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Date</label>
                                    <input className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold focus:ring-2 focus:ring-indigo-500 transition-all outline-none" type="date" value={modalData.holiday_date} onChange={e => setModalData({ ...modalData, holiday_date: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Region</label>
                                    <select className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all outline-none" value={modalData.country} onChange={e => setModalData({ ...modalData, country: e.target.value })}>
                                        {countryList.filter(c => c !== 'All Regions').map(country => (
                                            <option key={country} value={country}>{country}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex justify-end space-x-3 pt-2">
                                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition">Cancel</button>
                                    <button type="submit" className="px-8 py-3 bg-slate-900 text-white font-bold rounded-xl shadow-lg hover:bg-indigo-600 hover:shadow-indigo-500/30 transition-all transform hover:-translate-y-0.5">Save</button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default PolicySettings;